import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Pencil,
  Wifi,
  Clock,
  Shield,
  LayoutDashboard,
  BarChart3,
  Cloud,
  Wrench,
  Terminal,
  Tag,
  Calendar,
  Zap,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@mui/material";
import RefreshButton from "../../lib/components/RefreshButton";
import { DEFAULT_TIME_RANGE, RANGE_TYPE_LABELS } from "../../lib/utils/constants";
import { RESET_ZOOM_BUTTON_SX } from "../charger_metrics/constants";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider";
import { useTimezone } from "../../lib/context/TimezoneProvider";
import useChargerConfig from "./hooks/useChargerConfig";
import { formatDate, getTimezoneOffset, getRange, getRelativeTime, toUtcRange } from "../../lib/utils/utility";
import DateRangeToolbar from "../fleet_dashboard/DateRangeToolbar";
import { chargerDetailsMap, fieldGroups, GROUP_ICONS } from "./constants";
import { ChargerMetrics } from "../charger_metrics";
import OcppMessages from "../ocpp_messages";
import Events from "../events";
import SlideOutPanel from "../ocpp_messages/SlideOutPanel";
import { Troubleshoot } from "../logs_download";
import SendCommands from "./SendCommands";
import { fetchFleetSearchDetails } from "../fleet_metrics/hooks/useFleetSearchHook/api";
import chargerImg2 from "../../assets/charger_gen2.jpg";
import "./styles.scss";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { checkFeatureAccess, FEATURES, getCompanyDisplayName, isEnphaseUser } from "../../lib/utils/featureAccess";
import useHierarchy from "../../lib/hooks/useHierarchy";

const VALID_TABS = ["overview", "metrics", "ocpp", "troubleshoot", "events", "commands"];

const BASE_TAB_CONFIG = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "metrics", label: "Metrics", icon: BarChart3 },
  { id: "ocpp", label: "Cloud Logs", icon: Cloud },
  { id: "troubleshoot", label: "Device Logs", icon: Wrench },
  { id: "events", label: "Events", icon: Calendar },
  { id: "commands", label: "Commands", icon: Terminal, featureId: FEATURES.COMMANDS },
];

const CHARGER_IMAGES = [chargerImg2];

const objectDetailKeys = new Set(["wifiConfigDetails", "netConfigDetails", "cellularSettings"]);

const RECENT_CHARGERS_KEY = "evs-recent-chargers";
const MAX_RECENT = 3;

function loadRecentChargers() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_CHARGERS_KEY)) || [];
  } catch {
    return [];
  }
}

function removeRecentCharger(serial) {
  if (!serial) return;
  const list = loadRecentChargers().filter(c => c.serial !== serial);
  localStorage.setItem(RECENT_CHARGERS_KEY, JSON.stringify(list));
}

function saveRecentCharger(serial, siteId, status) {
  if (!serial) return;
  const list = loadRecentChargers().filter(c => c.serial !== serial);
  list.unshift({ serial, siteId: siteId || "-", status: status || null, ts: Date.now() });
  localStorage.setItem(RECENT_CHARGERS_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
}

export function EvSummary() {
  const { setState, serialNumber: globalSerialNumber, siteId: globalSiteId } = useGlobalFilters();
  const { effectiveTimezone } = useTimezone();
  const { chargerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { config, loading, error, hasFetched, sendCommand } = useChargerConfig(globalSiteId, globalSerialNumber);

  const enphaseUserFlag = isEnphaseUser();
  const tabConfig = BASE_TAB_CONFIG;

  const [isSerialEditorOpen, setIsSerialEditorOpen] = useState(false);
  const [serialQuery, setSerialQuery] = useState("");
  const [debouncedSerialQuery, setDebouncedSerialQuery] = useState("");
  const [fleetOptions, setFleetOptions] = useState([]);
  const [isSerialLoading, setIsSerialLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [commandHistory] = useState([]);
  const [stationName, setStationName] = useState(null);
  const [openChargerDetailsSlideout, setOpenChargerDetailsSlideout] = useState(false);
  const [recentChargers, setRecentChargers] = useState(loadRecentChargers);
  const serialEditorRef = useRef(null);
  const searchInputRef = useRef(null);

  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(VALID_TABS.includes(tabFromUrl) ? tabFromUrl : "overview");

  function setActiveTabWithParam(tab) {
    setActiveTab(tab);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: true }
    );
  }
  const [dateRange, setDateRange] = useState(() => getRange(DEFAULT_TIME_RANGE));

  // Push fresh dateRange to global state on mount so child tabs (Cloud Logs, Events)
  // that read globalDateRange get a current value on first load.
  const dateRangeSyncedRef = useRef(false);
  useEffect(() => {
    if (!dateRangeSyncedRef.current) {
      dateRangeSyncedRef.current = true;
      setState({ dateRange });
    }
  }, [dateRange, setState]);

  // DateRangeToolbar state
  const [activePreset, setActivePreset] = useState(DEFAULT_TIME_RANGE);

  const handlePresetChange = useCallback(
    value => {
      setActivePreset(value);
      const newRange = toUtcRange(getRange(value, effectiveTimezone), effectiveTimezone);
      setDateRange(newRange);
      setState({ dateRange: newRange });
    },
    [setState, effectiveTimezone]
  );

  const handleCustomChange = useCallback(
    (range, type) => {
      setDateRange(range);
      setActivePreset(type);
      setState({ dateRange: range });
    },
    [setState]
  );

  // Re-compute date range when effectiveTimezone changes
  const prevTimezoneRef = useRef(effectiveTimezone);
  useEffect(() => {
    if (effectiveTimezone && effectiveTimezone !== "UTC" && prevTimezoneRef.current !== effectiveTimezone) {
      prevTimezoneRef.current = effectiveTimezone;
      if (activePreset && activePreset !== RANGE_TYPE_LABELS.CUSTOM) {
        const newRange = toUtcRange(getRange(activePreset, effectiveTimezone), effectiveTimezone);
        setDateRange(newRange);
        setState({ dateRange: newRange });
      }
    }
  }, [effectiveTimezone, activePreset, setState]);

  const hasSelectedChargerContext = Boolean(chargerId || globalSerialNumber);
  const notFound = hasSelectedChargerContext && hasFetched && !loading && (error || (!config && !error));

  const companyName = getCompanyDisplayName();

  useEffect(() => {
    if (chargerId && chargerId !== globalSerialNumber) {
      setState({ serialNumber: chargerId });
    }
  }, [chargerId, globalSerialNumber, setState]);

  // Sync siteId from config whenever it changes (handles charger switches & direct serial entry)
  // Guard: only apply when config belongs to the currently selected charger to avoid
  // stale config from the previous charger overwriting siteId during the debounce window.
  useEffect(() => {
    if (config?.siteId && config.siteId !== globalSiteId && config.serialNumber === globalSerialNumber) {
      setState({ siteId: config.siteId });
    }
  }, [config?.siteId, config?.serialNumber, globalSiteId, globalSerialNumber, setState]);

  // Set siteTimezone in global state when charger config loads
  useEffect(() => {
    if (config?.timezone && config.serialNumber === globalSerialNumber) {
      setState({ siteTimezone: config.timezone });
    }
  }, [config?.timezone, config?.serialNumber, globalSerialNumber, setState]);

  // Save to recent chargers when config loads with valid data
  useEffect(() => {
    if (config?.siteId && globalSerialNumber) {
      saveRecentCharger(globalSerialNumber, config.siteId, config.status);
      setRecentChargers(loadRecentChargers());
    }
  }, [config, globalSerialNumber]);

  // Remove from recent chargers if the API returns no details — avoids
  // showing stale siteId / status from a previous successful search.
  useEffect(() => {
    if (notFound && globalSerialNumber) {
      removeRecentCharger(globalSerialNumber);
      setRecentChargers(loadRecentChargers());
    }
  }, [notFound, globalSerialNumber]);

  // Deterministic charger image based on serial number
  const chargerImage = useMemo(() => {
    const seed = globalSerialNumber || "";
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = (hash << 5) - hash + seed.charCodeAt(i);
      hash |= 0;
    }
    return CHARGER_IMAGES[Math.abs(hash) % CHARGER_IMAGES.length];
  }, [globalSerialNumber]);

  function renderDashboardSkeleton() {
    return (
      <div className="evs-skeleton">
        <div className="skel skel-hero" />
        <div className="skel skel-tabs" />
        <div className="skel-stats-row">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skel skel-stat" style={{ animationDelay: `${i * 0.1}s` }} />
          ))}
        </div>
        <div className="skel-groups-row">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skel skel-card" style={{ animationDelay: `${i * 0.1}s` }} />
          ))}
        </div>
      </div>
    );
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSerialQuery(serialQuery);
    }, 250);

    return () => clearTimeout(handler);
  }, [serialQuery]);

  useEffect(() => {
    if (!isSerialEditorOpen && (globalSerialNumber || config?.serialNumber)) {
      setSerialQuery(globalSerialNumber || config?.serialNumber || "");
    }
  }, [globalSerialNumber, config?.serialNumber, isSerialEditorOpen]);

  // Resolve station name from cached hierarchy data
  const { stations: hierarchyStations } = useHierarchy({ enabled: !enphaseUserFlag });

  useEffect(() => {
    const siteId = config?.siteId || globalSiteId;
    if (siteId && hierarchyStations.length > 0) {
      let foundStationName = null;
      hierarchyStations.forEach(station => {
        if (station.sites && Array.isArray(station.sites)) {
          station.sites.forEach(site => {
            if (site.siteId === siteId) {
              foundStationName = station.stationName || station.name || "Unknown Station";
            }
          });
        }
      });
      setStationName(foundStationName);
    } else if (!siteId) {
      setStationName(null);
    }
  }, [config?.siteId, globalSiteId, hierarchyStations]);

  const dateKeys = new Set([
    "lastReportedAt",
    "createdAt",
    "warrantyDueDate",
    "warrantyStartDate",
    "updatedAt",
    "fwUpdatedAt",
  ]);

  function formatValue(key, value) {
    if (key === "timezone") {
      return `${value} (${getTimezoneOffset(value)})`;
    }
    if (key === "phaseMode") {
      const numValue = Number(value);
      if (numValue === 1 || numValue === 3) return "IT";
      if (numValue === 2 || numValue === 4) return "TN/TT";
      return value || "-";
    }
    if (key === "hoControl") {
      return !value ? "Yes" : "No";
    }
    if (key === "commissioningStatus") {
      const num = Number(value);
      if (num === 1) return "Commissioned";
      if (num === 0) return "Not Commissioned";
      return value !== null && value !== undefined ? String(value) : "-";
    }
    if (key === "dlbEnabled") {
      const num = Number(value);
      if (num === 1) return "Yes";
      if (num === 0) return "No";
      return value !== null && value !== undefined ? String(value) : "-";
    }
    if (key === "isConnected") {
      return config?.activeConnection || (value ? "Yes" : "No");
    }
    if (key === "ratedCurrent") {
      if (value !== null && value !== undefined) return `${value}A`;
      return "-";
    }
    if (dateKeys.has(key)) return formatDate(value);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return value !== null && value !== undefined ? String(value) : "-";
  }

  async function onSerialEditorOpen() {
    setIsSerialEditorOpen(true);

    // V2 (Enphase users) doesn't need the ev_charger fleet search API
    if (enphaseUserFlag) return;

    if (fleetOptions.length > 0) return;

    setIsSerialLoading(true);
    try {
      const response = await fetchFleetSearchDetails();
      const data = Array.isArray(response?.data) ? response.data : response?.data?.data || [];
      setFleetOptions(Array.isArray(data) ? data : []);
    } catch {
      setFleetOptions([]);
    } finally {
      setIsSerialLoading(false);
    }
  }

  function onSerialSelect(option) {
    if (!option?.serialNumber) return;

    setIsSerialEditorOpen(false);
    setSerialQuery(option.serialNumber);
    setState({ serialNumber: option.serialNumber, siteId: option.siteId });
    navigate(`/chargers/${encodeURIComponent(option.serialNumber)}`, { replace: true });
  }

  function handleSearchKeyPress(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      const query = serialQuery.trim();
      if (!query) return;

      // Try to find a match in pre-loaded options (for siteId)
      const match = fleetOptions.find(
        option =>
          option.serialNumber?.toLowerCase() === query.toLowerCase() ||
          option.serialNumber?.toLowerCase().includes(query.toLowerCase())
      );

      if (match) {
        onSerialSelect(match);
      } else {
        // No match in suggestions — use the raw query as serial number
        setIsSerialEditorOpen(false);
        setState({ serialNumber: query, siteId: undefined });
        navigate(`/chargers/${encodeURIComponent(query)}`, { replace: true });
      }
    }
  }

  const serialSuggestions = useMemo(() => {
    const query = debouncedSerialQuery.trim().toLowerCase();
    if (!query) return [];
    const filtered = fleetOptions.filter(option => {
      const serial = option?.serialNumber ? String(option.serialNumber).toLowerCase() : "";
      if (!serial) return false;
      return query ? serial.includes(query) : true;
    });
    return filtered.slice(0, 5);
  }, [debouncedSerialQuery, fleetOptions]);

  useEffect(() => {
    if (!isSerialEditorOpen) return;

    const handleOutsideClick = event => {
      if (serialEditorRef.current && !serialEditorRef.current.contains(event.target)) {
        setIsSerialEditorOpen(false);
      }
    };

    window.addEventListener("mousedown", handleOutsideClick);
    return () => window.removeEventListener("mousedown", handleOutsideClick);
  }, [isSerialEditorOpen]);

  const handleRefreshClick = useCallback(() => {
    if (activePreset === RANGE_TYPE_LABELS.CUSTOM) {
      // For custom ranges, re-trigger fetch with the existing dateRange
      setState({ dateRange: [...dateRange] });
    } else {
      const newRange = toUtcRange(getRange(activePreset || DEFAULT_TIME_RANGE, effectiveTimezone), effectiveTimezone);
      setDateRange(newRange);
      setState({ dateRange: newRange });
    }
  }, [activePreset, dateRange, effectiveTimezone, setState]);

  const getStatusColor = status => {
    if (!status) return "";
    if (status === "NORMAL") return "green";
    if (status === "OFFLINE") return "gray";
    if (status === "FAULTED") return "red";
    return "red";
  };

  const getStatusLabel = status => {
    if (!status) return "-";
    if (status === "NORMAL") return "Online";
    if (status === "OFFLINE") return "Offline";
    if (status === "FAULTED") return "Faulted";
    return status;
  };

  const shouldShowSuggestions = isSerialEditorOpen && (serialQuery.trim().length > 0 || isSerialLoading);
  const isHydratingSelectedCharger = hasSelectedChargerContext && (loading || !hasFetched);
  const showSearchLanding = !hasSelectedChargerContext && !config && !loading;

  function onRecentChargerClick(item) {
    setSerialQuery(item.serial);
    setState({ serialNumber: item.serial, siteId: item.siteId !== "-" ? item.siteId : undefined });
    navigate(`/chargers/${encodeURIComponent(item.serial)}`, { replace: true });
  }

  function getValueClassName(key, value) {
    const formatted = formatValue(key, value);
    if (formatted === "Yes") return "evs-value--yes";
    if (formatted === "No") return "evs-value--no";
    if (key === "isConnected" && value === true) return "evs-value--connected";
    if (key === "isConnected" && value === false) return "evs-value--disconnected";
    if (key === "status" && value === "NORMAL") return "evs-value--normal";
    return "";
  }

  function renderOverviewTab(data) {
    if (!data) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* KPI Cards Row — matches Dashboard kpi-card style */}
        <div className="kpi-grid kpi-grid--charger">
          <motion.div
            className="kpi-card"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <div className="kpi-card__icon kpi-card__icon--green">
              <Tag size={22} />
            </div>
            <div className="kpi-card__content">
              <span className="kpi-card__label">SKU</span>
              <div className="kpi-card__value-row">
                <span className="kpi-card__value kpi-card__value--sm">{data.sku || "-"}</span>
              </div>
            </div>
          </motion.div>
          <motion.div
            className="kpi-card"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="kpi-card__icon kpi-card__icon--orange">
              <Wifi size={22} />
            </div>
            <div className="kpi-card__content">
              <span className="kpi-card__label">Connection</span>
              <div className="kpi-card__value-row">
                <span className="kpi-card__value kpi-card__value--sm">{data.activeConnection || "-"}</span>
              </div>
            </div>
          </motion.div>
          <motion.div
            className="kpi-card"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="kpi-card__icon kpi-card__icon--blue">
              <Clock size={22} />
            </div>
            <div className="kpi-card__content">
              <span className="kpi-card__label">Last Reported</span>
              <div className="kpi-card__value-row">
                <span
                  className="kpi-card__value kpi-card__value--sm"
                  title={data.lastReportedAt ? formatDate(data.lastReportedAt) : ""}
                >
                  {data.lastReportedAt ? getRelativeTime(data.lastReportedAt) : "-"}
                </span>
              </div>
            </div>
          </motion.div>
          <motion.div
            className="kpi-card"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="kpi-card__icon kpi-card__icon--purple">
              <Shield size={22} />
            </div>
            <div className="kpi-card__content">
              <span className="kpi-card__label">Phase Config</span>
              <div className="kpi-card__value-row">
                <span className="kpi-card__value kpi-card__value--sm">
                  {data.phaseCount ? `${data.phaseCount}-Phase` : "-"}
                  {data.ratedCurrent ? ` / ${data.ratedCurrent}A` : ""}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Data as of timestamp */}
        {data.lastReportedAt && (
          <motion.div
            className="evs-data-as-of"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Data as of {formatDate(data.lastReportedAt)}
          </motion.div>
        )}

        {/* Detail Group Cards — two fixed columns */}
        <div className="evs-group-grid">
          {["left", "right"].map(side => {
            const columnGroups =
              side === "left"
                ? fieldGroups.filter(g => g.title === "Device Information" || g.title === "Partner Integration")
                : fieldGroups.filter(g => g.title !== "Device Information" && g.title !== "Partner Integration");
            return (
              <div key={side} className="evs-group-col">
                {columnGroups.map((group, gi) => {
                  const groupIcon = GROUP_ICONS[group.title];
                  return (
                    <motion.div
                      key={group.title}
                      className="evs-group-card"
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.1 + gi * 0.06, duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div className="evs-group-header">
                        {groupIcon && (
                          <div className={`evs-group-icon evs-group-icon--${groupIcon.color}`}>{groupIcon.emoji}</div>
                        )}
                        <div className="evs-group-title">{group.title}</div>
                      </div>
                      <div className="evs-group-rows">
                        {group.keys.map(key => {
                          if (!chargerDetailsMap[key]) return null;
                          const val = data?.[key];

                          // Object-type fields — render as key:value pairs
                          if (objectDetailKeys.has(key)) {
                            if (!val || typeof val !== "object") return null;
                            return (
                              <div key={key} className="evs-group-row evs-group-row--block">
                                <div className="evs-row-label">{chargerDetailsMap[key]}</div>
                                <div className="evs-kv-list">
                                  {Object.entries(val).map(([k, v]) => (
                                    <div key={k} className="evs-kv-item">
                                      <span className="evs-kv-key">{k}</span>
                                      <span className="evs-kv-val">
                                        {v !== null && v !== undefined ? String(v) : "-"}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          }

                          return (
                            <div key={key} className="evs-group-row">
                              <div className="evs-row-label">{chargerDetailsMap[key]}</div>
                              <div className={`evs-row-value ${getValueClassName(key, val)}`}>
                                {formatValue(key, val)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </motion.div>
    );
  }

  return (
    <div className="ev-summary-wrapper">
      {/* Compact Hero Banner — matches Dashboard & Fleet Search */}
      <div className="evs-hero evs-hero--compact">
        <svg className="evs-hero__particles" aria-hidden="true">
          {Array.from({ length: 15 }).map((_, i) => (
            <circle
              key={i}
              className="evs-hero__particle"
              cx={`${6 + ((i * 31) % 90)}%`}
              cy={`${8 + ((i * 17) % 85)}%`}
              r={1 + (i % 3)}
              style={{ animationDelay: `${(i * 0.4) % 6}s`, animationDuration: `${4 + (i % 4)}s` }}
            />
          ))}
        </svg>
        <div className="evs-hero__content">
          <div className="evs-hero__text">
            <span className="evs-hero__company">{companyName}</span>
            <h1 className="evs-hero__title">Charger Summary</h1>
            <p className="evs-hero__subtitle">Detailed charger configuration & monitoring</p>
          </div>
          {!loading && !error && config && (
            <>
              <div className="evs-hero__image-wrapper">
                <div className="evs-hero__glow-ring" />
                <img className="evs-hero__charger-img" src={chargerImage} alt="EV Charger" />
              </div>
              <div className="evs-hero__live-stats">
                <div className="evs-hero__detail" ref={serialEditorRef}>
                  <span className="evs-hero__detail-label">Serial</span>
                  <span className="evs-hero__detail-value">
                    {globalSerialNumber || config?.serialNumber || "-"}
                    <button className="evs-hero__inline-btn" onClick={onSerialEditorOpen} title="Change serial number">
                      <Pencil size={11} />
                    </button>
                  </span>
                  {isSerialEditorOpen && (
                    <div className="evs-hero__search-popover">
                      <div className="evs-hero__search-input-wrapper">
                        <Search size={14} />
                        <input
                          type="text"
                          value={serialQuery}
                          onChange={e => setSerialQuery(e.target.value)}
                          onKeyPress={handleSearchKeyPress}
                          placeholder="Search for device..."
                          autoFocus // eslint-disable-line jsx-a11y/no-autofocus
                        />
                        {isSerialLoading && <div className="evs-hero__search-spinner" />}
                      </div>
                      {shouldShowSuggestions && (
                        <div className="evs-hero__search-results">
                          {isSerialLoading ? (
                            <div className="evs-hero__search-loading">Loading devices...</div>
                          ) : serialSuggestions.length > 0 ? (
                            serialSuggestions.map((option, index) => (
                              <div
                                key={`${option.serialNumber}-${index}`}
                                className="evs-hero__search-item"
                                role="button"
                                tabIndex={0}
                                onClick={() => onSerialSelect(option)}
                                onKeyDown={e => {
                                  if (e.key === "Enter" || e.key === " ") onSerialSelect(option);
                                }}
                              >
                                <div className="evs-hero__search-item-serial">{option.serialNumber}</div>
                                <div className="evs-hero__search-item-site">Site: {option.siteId}</div>
                              </div>
                            ))
                          ) : (
                            <div className="evs-hero__search-loading">No devices found</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="evs-hero__stat-divider" />
                <div className="evs-hero__detail">
                  <span className="evs-hero__detail-label">Site</span>
                  <span className="evs-hero__detail-value">{config?.siteId || globalSiteId || "-"}</span>
                </div>
                {stationName && !enphaseUserFlag && (
                  <>
                    <div className="evs-hero__stat-divider" />
                    <div className="evs-hero__detail">
                      <span className="evs-hero__detail-label">Station</span>
                      <span className="evs-hero__detail-value">{stationName}</span>
                    </div>
                  </>
                )}
                <div className="evs-hero__stat-divider" />
                <div className="evs-hero__detail">
                  <span className="evs-hero__detail-label">Status</span>
                  <span className="evs-hero__detail-value">
                    <span className={`evs-hero__badge evs-hero__badge--${getStatusColor(config?.status) || "default"}`}>
                      {config?.status === "NORMAL" && <span className="evs-hero__badge-dot" />}
                      {getStatusLabel(config?.status)}
                    </span>
                  </span>
                </div>
                {config?.firmwareVersion && (
                  <>
                    <div className="evs-hero__stat-divider" />
                    <div className="evs-hero__detail">
                      <span className="evs-hero__detail-label">Firmware</span>
                      <span className="evs-hero__detail-value">{config.firmwareVersion}</span>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Pill Tabs */}
      {!loading && !error && config && (
        <motion.div
          className="evs-tabs"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="evs-tabs__inner">
            {tabConfig.map(tab => {
              if (tab.featureId && !checkFeatureAccess(tab.featureId)) return null;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`evs-tabs__btn ${activeTab === tab.id ? "evs-tabs__btn--active" : ""}`}
                  onClick={() => setActiveTabWithParam(tab.id)}
                >
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Content Area */}
      <div className="content-area">
        {!error && (loading || isHydratingSelectedCharger) && renderDashboardSkeleton()}
        {notFound && (
          <motion.div
            className="evs-landing"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="evs-landing__card">
              <div className="evs-landing__icon evs-landing__icon--warn">
                <AlertTriangle size={32} />
              </div>
              <h2 className="evs-landing__title">No Details Found</h2>
              <p className="evs-landing__subtitle">
                No charger details were found for <strong>{globalSerialNumber || chargerId}</strong>. Please check the
                serial number and try again.
              </p>

              {/* Search input */}
              <div className="evs-landing__search" ref={serialEditorRef}>
                <div className="evs-landing__search-input">
                  <Search size={18} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={serialQuery}
                    onChange={e => {
                      setSerialQuery(e.target.value);
                      if (!isSerialEditorOpen) onSerialEditorOpen();
                    }}
                    onKeyPress={handleSearchKeyPress}
                    onFocus={() => setIsSerialEditorOpen(true)}
                    placeholder="Try another serial number..."
                  />
                  {isSerialLoading && <div className="evs-landing__spinner" />}
                  <button
                    className="evs-landing__search-btn"
                    onClick={() => {
                      const query = serialQuery.trim();
                      if (query) handleSearchKeyPress({ key: "Enter", preventDefault: () => {} });
                      else searchInputRef.current?.focus();
                    }}
                    title="Search"
                  >
                    <ArrowRight size={18} />
                  </button>
                </div>

                {shouldShowSuggestions && (
                  <div className="evs-landing__suggestions">
                    {isSerialLoading ? (
                      <div className="evs-landing__suggestions-empty">Loading devices...</div>
                    ) : serialSuggestions.length > 0 ? (
                      serialSuggestions.map((option, index) => (
                        <div
                          key={`${option.serialNumber}-${index}`}
                          className="evs-landing__suggestion-item"
                          role="button"
                          tabIndex={0}
                          onClick={() => onSerialSelect(option)}
                          onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") onSerialSelect(option);
                          }}
                        >
                          <Zap size={14} />
                          <div>
                            <div className="evs-landing__suggestion-serial">{option.serialNumber}</div>
                            <div className="evs-landing__suggestion-site">Site: {option.siteId}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="evs-landing__suggestions-empty">No devices found</div>
                    )}
                  </div>
                )}
              </div>

              {recentChargers.length > 0 && (
                <div className="evs-landing__recent">
                  <h4 className="evs-landing__recent-title">Recent Chargers</h4>
                  <div className="evs-landing__recent-list">
                    {recentChargers.slice(0, 3).map(item => (
                      <button
                        key={item.serial}
                        className="evs-landing__recent-item"
                        onClick={() => onRecentChargerClick(item)}
                      >
                        <Zap size={14} className="evs-landing__recent-icon" />
                        <span className="evs-landing__recent-serial">{item.serial}</span>
                        <span className="evs-landing__recent-site">{item.siteId}</span>
                        {item.status && (
                          <span
                            className={`evs-landing__recent-status evs-landing__recent-status--${getStatusColor(item.status)}`}
                          >
                            {getStatusLabel(item.status)}
                          </span>
                        )}
                        <ArrowRight size={14} className="evs-landing__recent-arrow" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
        {showSearchLanding && (
          <motion.div
            className="evs-landing"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="evs-landing__card">
              <div className="evs-landing__icon">
                <Zap size={32} />
              </div>
              <h2 className="evs-landing__title">No Charger Selected</h2>
              <p className="evs-landing__subtitle">
                Enter a serial number to view configuration, metrics, and send commands.
              </p>

              {/* Search input */}
              <div className="evs-landing__search" ref={serialEditorRef}>
                <div className="evs-landing__search-input">
                  <Search size={18} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={serialQuery}
                    onChange={e => {
                      setSerialQuery(e.target.value);
                      if (!isSerialEditorOpen) onSerialEditorOpen();
                    }}
                    onKeyPress={handleSearchKeyPress}
                    onFocus={() => setIsSerialEditorOpen(true)}
                    placeholder="Enter serial number..."
                  />
                  {isSerialLoading && <div className="evs-landing__spinner" />}
                  <button
                    className="evs-landing__search-btn"
                    onClick={() => {
                      const query = serialQuery.trim();
                      if (query) handleSearchKeyPress({ key: "Enter", preventDefault: () => {} });
                      else searchInputRef.current?.focus();
                    }}
                    title="Search"
                  >
                    <ArrowRight size={18} />
                  </button>
                </div>

                {/* Suggestions dropdown */}
                {shouldShowSuggestions && (
                  <div className="evs-landing__suggestions">
                    {isSerialLoading ? (
                      <div className="evs-landing__suggestions-empty">Loading devices...</div>
                    ) : serialSuggestions.length > 0 ? (
                      serialSuggestions.map((option, index) => (
                        <div
                          key={`${option.serialNumber}-${index}`}
                          className="evs-landing__suggestion-item"
                          role="button"
                          tabIndex={0}
                          onClick={() => onSerialSelect(option)}
                          onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") onSerialSelect(option);
                          }}
                        >
                          <Zap size={14} />
                          <div>
                            <div className="evs-landing__suggestion-serial">{option.serialNumber}</div>
                            <div className="evs-landing__suggestion-site">Site: {option.siteId}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="evs-landing__suggestions-empty">No devices found</div>
                    )}
                  </div>
                )}
              </div>

              {/* Recent chargers */}
              {recentChargers.length > 0 && (
                <div className="evs-landing__recent">
                  <h4 className="evs-landing__recent-title">Recent Chargers</h4>
                  <div className="evs-landing__recent-list">
                    {recentChargers.slice(0, 3).map(item => (
                      <button
                        key={item.serial}
                        className="evs-landing__recent-item"
                        onClick={() => onRecentChargerClick(item)}
                      >
                        <Zap size={14} className="evs-landing__recent-icon" />
                        <span className="evs-landing__recent-serial">{item.serial}</span>
                        <span className="evs-landing__recent-site">{item.siteId}</span>
                        {item.status && (
                          <span
                            className={`evs-landing__recent-status evs-landing__recent-status--${getStatusColor(item.status)}`}
                          >
                            {getStatusLabel(item.status)}
                          </span>
                        )}
                        <ArrowRight size={14} className="evs-landing__recent-arrow" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
        {!loading && !error && config && (
          <>
            {activeTab === "overview" && renderOverviewTab(config)}
            {activeTab === "metrics" && (
              <div className="evs-metrics-content">
                <ChargerMetrics
                  embedded={true}
                  dateRange={dateRange}
                  headerActions={({ zoomExtremes, setZoomExtremes }) => (
                    <div className="evs-metrics-header">
                      <DateRangeToolbar
                        activePreset={activePreset}
                        onPresetChange={handlePresetChange}
                        onCustomChange={handleCustomChange}
                        showTimezone
                      />
                      <div className="evs-metrics-actions">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!zoomExtremes}
                          onClick={() => setZoomExtremes(null)}
                          sx={RESET_ZOOM_BUTTON_SX}
                        >
                          Reset Zoom
                        </Button>
                        <RefreshButton onClick={handleRefreshClick} />
                      </div>
                    </div>
                  )}
                />
              </div>
            )}
            {activeTab === "ocpp" && (
              <>
                <div className="evs-ocpp-content"></div>
                <OcppMessages
                  activePreset={activePreset}
                  onPresetChange={handlePresetChange}
                  onCustomChange={handleCustomChange}
                  onRefresh={handleRefreshClick}
                />
              </>
            )}
            {activeTab === "troubleshoot" && <Troubleshoot />}
            {activeTab === "events" && (
              <Events
                activePreset={activePreset}
                onPresetChange={handlePresetChange}
                onCustomChange={handleCustomChange}
                onRefresh={handleRefreshClick}
              />
            )}
            {activeTab === "commands" && checkFeatureAccess(FEATURES.COMMANDS) && (
              <>
                <SendCommands
                  serialNumber={globalSerialNumber}
                  siteId={globalSiteId}
                  sendCommand={sendCommand}
                  ratedCurrent={config?.ratedCurrent}
                />
                {commandHistory.length > 0 && (
                  <motion.div className="evs-cmd-history" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <div className="evs-cmd-history__title">Recent Command Activity</div>
                    {commandHistory.map(entry => (
                      <div key={entry.id} className={`evs-cmd-history__item evs-cmd-history__item--${entry.status}`}>
                        <span className="evs-cmd-history__action">
                          {entry.action === "restart" ? "Restart" : "Get Config"}
                        </span>
                        <span className={`evs-cmd-history__status evs-cmd-history__status--${entry.status}`}>
                          {entry.status === "pending"
                            ? "Sending..."
                            : entry.status === "success"
                              ? `✓ ${entry.result}`
                              : `✗ ${entry.result}`}
                        </span>
                        <span className="evs-cmd-history__time">{getRelativeTime(entry.time)}</span>
                      </div>
                    ))}
                  </motion.div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {openChargerDetailsSlideout && (
        <SlideOutPanel open={openChargerDetailsSlideout} onClose={() => setOpenChargerDetailsSlideout(false)} />
      )}
    </div>
  );
}

export default EvSummary;
